import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import Breadcrumb from '../../components/Breadcrumb';
import Footer from '../../components/Footer';
import Logo from '../../assets/images/logos/logo2.png';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const INSTITUTION_ATTENDED_HISTORY_API = process.env.REACT_APP_INSTITUTION_ATTENDED_HISTORY_API || `${API_BASE_URL}/api/admin-app/institution-student-attended-history/`;
const INSTITUTION_QUIZ_REASSIGN_API = process.env.REACT_APP_INSTITUTION_QUIZ_REASSIGN_API || `${API_BASE_URL}/api/admin-app/institution-quiz-reassign/`;

const InstitutionTestHistoryPage = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const collegeCode = useMemo(
        () => (localStorage.getItem('guest_college_code') || sessionStorage.getItem('guest_college_code') || '').trim(),
        []
    );

    const handleLogout = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will be logged out of your account.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#1e40af',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, logout'
        }).then((result) => {
            if (result.isConfirmed) {
            localStorage.removeItem('guest_user_role');
            localStorage.removeItem('guest_access_token');
            localStorage.removeItem('guest_refresh_token');
            localStorage.removeItem('guest_college_code');
            localStorage.removeItem('guest_college_name');
            sessionStorage.removeItem('guest_user_role');
            sessionStorage.removeItem('guest_college_code');
            sessionStorage.removeItem('guest_college_name');
            Swal.fire({
                title: 'Logged Out',
                text: 'You have successfully logged out.',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
                confirmButtonColor: '#1e40af'
            }).then(() => {
                navigate('/login');
            });
            }
        });
    };

    useEffect(() => {
        const fetchAttendedHistory = async () => {
            try {
                setLoading(true);
                setErrorMessage('');

                if (!collegeCode) {
                    setRows([]);
                    setErrorMessage('College session not found. Please log in again.');
                    return;
                }

                const url = `${INSTITUTION_ATTENDED_HISTORY_API}?college_code=${encodeURIComponent(collegeCode)}`;
                const response = await fetch(url, { method: 'GET' });
                if (!response.ok) {
                    throw new Error('Unable to load attended history.');
                }

                const data = await response.json();
                setRows(Array.isArray(data) ? data : []);
            } catch (error) {
                setRows([]);
                setErrorMessage('Failed to fetch student attended history. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchAttendedHistory();
    }, [collegeCode]);

    const handleReassign = async (row) => {
        const result = await Swal.fire({
            title: 'Reassign this quiz?',
            text: `This will allow ${row.student_name || row.student_email || 'this student'} to attend the same quiz again.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, reassign',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1e40af',
        });

        if (!result.isConfirmed) return;

        try {
            setActionLoadingId(row.id);
            const response = await fetch(INSTITUTION_QUIZ_REASSIGN_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    college_code: collegeCode,
                    test_id: row.test_id,
                    student_id: row.student_id || undefined,
                    student_email: row.student_email || undefined,
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                await Swal.fire('Error', data?.error || 'Unable to reassign quiz right now.', 'error');
                return;
            }

            await Swal.fire('Reassigned', 'The student can now attend the quiz again.', 'success');
            const refreshed = await fetch(`${INSTITUTION_ATTENDED_HISTORY_API}?college_code=${encodeURIComponent(collegeCode)}`);
            const refreshedData = refreshed.ok ? await refreshed.json() : [];
            setRows(Array.isArray(refreshedData) ? refreshedData : []);
        } catch (error) {
            await Swal.fire('Error', 'Unable to reassign quiz right now.', 'error');
        } finally {
            setActionLoadingId(null);
        }
    };

    return (
        <div className="institution-monitoring-page">
            <header className="header-area">
                <div className="menu-part" style={{ borderBottom: '1px solid #E8ECF4', background: '#fff' }}>
                    <div className="container">
                        <div style={{ padding: '14px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Link to="/dashboard/institution" className="logo-text">
                                <img src={Logo} alt="Institution Logo" style={{ height: '55px' }} />
                            </Link>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="react-btn-primary"
                                style={{
                                    background: '#1e40af',
                                    border: '2px solid #1e3a8a',
                                    padding: '8px 20px',
                                    borderRadius: '8px',
                                    color: '#fff',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                                    textDecoration: 'none',
                                    display: 'inline-block',
                                    textTransform: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <Breadcrumb pageTitle="Student Attended History" />

            <div className="container mt-4 mb-5">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                    <div>
                        <h2 className="mb-1">Student Attended History</h2>
                        <p className="mb-0 text-muted">Records of students who submitted or attended quizzes under this institution.</p>
                    </div>
                    <Link to="/dashboard/institution?role=institution" className="btn btn-primary">
                        Back to Dashboard
                    </Link>
                </div>

                {errorMessage ? (
                    <div className="alert alert-danger" role="alert">
                        {errorMessage}
                    </div>
                ) : null}

                <div className="card shadow-sm border-0">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Student Name</th>
                                        <th>Email</th>
                                        <th>Quiz</th>
                                        <th>Submitted At</th>
                                        <th className="text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">Loading attended history...</td>
                                        </tr>
                                    ) : rows.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-4">No attended history found.</td>
                                        </tr>
                                    ) : (
                                        rows.map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.student_name || '-'}</td>
                                                <td>{row.student_email || '-'}</td>
                                                <td>{row.test_name || `Quiz #${row.test_id}`}</td>
                                                <td>{row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '-'}</td>
                                                <td className="text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-primary"
                                                        onClick={() => handleReassign(row)}
                                                        disabled={actionLoadingId === row.id}
                                                    >
                                                        {actionLoadingId === row.id ? 'Working...' : 'Reassign'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <Footer footerClass="react-footer home-main student-mobile-fixed-footer" />
        </div>
    );
};

export default InstitutionTestHistoryPage;